/**
 * Конвертация выбранного периода (`PeriodPopoverState`) в диапазон дат
 * `[dateFrom, dateTo)` (ISO) для analytics-эндпоинтов и фильтра ленты.
 *
 * Диапазон — КАЛЕНДАРНЫЙ и honORует конкретный выбор поповера
 * (`year`/`monthIdx`/`quarter`/`current`/`preset`/`custom*`), а не только `kind`.
 * `dateTo` — начало суток за последним включённым днём (полуинтервал).
 *
 * В mock-режиме «сегодня» прибито к `MOCK_TODAY` (детерминизм моков);
 * в реальном режиме используется настоящая текущая дата. Границы выровнены
 * по началу суток, чтобы ключи запросов были стабильны в течение дня.
 */
import type {PeriodPopoverState} from '@lib/atoms';
import {MOCK_TODAY} from '@lib/utils';
import {IS_MOCK} from '../api/mock-mode';

/** Диапазон дат периода: `[dateFrom, dateTo)` в ISO. */
export interface PeriodRange {
  dateFrom: string;
  dateTo: string;
}

/** Начало суток для даты (локальная полночь). */
const startOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Следующие сутки (для эксклюзивной верхней границы). */
const nextDay = (d: Date): Date => {
  const e = new Date(d);
  e.setDate(e.getDate() + 1);
  return e;
};

/** «YYYY-MM-DD» → локальная полночь этого дня (без TZ-сдвига парсинга Date). */
const parseIsoDay = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};

/** «Сегодня» источника данных: реальная дата либо MOCK_TODAY в mock-режиме. */
export const dataToday = (): Date =>
  IS_MOCK ? new Date(MOCK_TODAY) : new Date();

/**
 * Календарный диапазон `dateFrom/dateTo` для выбранного периода поповера.
 * Полуинтервал `[dateFrom, dateTo)`; верхняя граница — начало суток за концом.
 */
export const periodRange = (state: PeriodPopoverState): PeriodRange => {
  const today = startOfDay(dataToday());
  const iso = (d: Date): string => d.toISOString();

  switch (state.kind) {
    case 'day': {
      if (state.exactRange) return state.exactRange;
      // Произвольный диапазон дат либо пресет «последние N дней до сегодня».
      if (state.customStart && state.customEnd) {
        const start = parseIsoDay(state.customStart);
        const end = nextDay(parseIsoDay(state.customEnd));
        return {dateFrom: iso(start), dateTo: iso(end)};
      }
      const preset = state.preset ?? 7;
      const start = new Date(today);
      start.setDate(start.getDate() - preset + 1);
      return {dateFrom: iso(start), dateTo: iso(nextDay(today))};
    }
    case 'week': {
      // Календарная неделя (пн–вс): текущая либо прошедшая.
      const dowMon = (today.getDay() + 6) % 7; // 0..6 от понедельника
      const monday = new Date(today);
      monday.setDate(monday.getDate() - dowMon - (state.current ? 0 : 7));
      const end = new Date(monday);
      end.setDate(end.getDate() + 7);
      return {dateFrom: iso(monday), dateTo: iso(end)};
    }
    case 'month': {
      const start = new Date(state.year, state.monthIdx, 1);
      const end = new Date(state.year, state.monthIdx + 1, 1);
      return {dateFrom: iso(start), dateTo: iso(end)};
    }
    case 'quarter': {
      const firstMonth = (state.quarter - 1) * 3;
      const start = new Date(state.year, firstMonth, 1);
      const end = new Date(state.year, firstMonth + 3, 1);
      return {dateFrom: iso(start), dateTo: iso(end)};
    }
    case 'year': {
      const start = new Date(state.year, 0, 1);
      const end = new Date(state.year + 1, 0, 1);
      return {dateFrom: iso(start), dateTo: iso(end)};
    }
  }
};

/**
 * Скользящее окно «последние N дней до текущего момента». Для период-независимых
 * выборок (напр. очередей внимания), где нужен стабильный ключ запроса, не
 * зависящий от выбранного периода аналитики.
 */
export const recentDaysRange = (days: number): PeriodRange => {
  const today = startOfDay(dataToday());
  const start = new Date(today);
  start.setDate(start.getDate() - days + 1);
  return {dateFrom: start.toISOString(), dateTo: nextDay(today).toISOString()};
};
